import { axiosInstance } from "@/use/utilities/axios/app/axios-instance";
import { axiosInstanceWithoutLoader } from "@/use/utilities/axios/app/axios-instance-without-loader";

async function unwrapError(error) {
  const responseData = error.response?.data;

  if (responseData instanceof Blob) {
    let parsed = null;

    try {
      parsed = JSON.parse(await responseData.text());
    } catch {
      // Fall back to the original axios error shape when the blob is not JSON.
    }

    if (parsed) {
      throw parsed.errors ?? parsed.message ?? error;
    }
  }

  throw error.response?.data?.errors ?? error.response?.data?.message ?? error;
}

export async function getForm() {
  const axios = axiosInstance().primoLab;
  try {
    const response = await axios.get("/cam-production/form");
    return { data: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function listLogs(filters = {}, loading = true) {
  const axios = loading ? axiosInstance().primoLab : axiosInstanceWithoutLoader().primoLab;
  try {
    const response = await axios.get("/cam-production/logs", {
      params: {
        month: filters.month || undefined,
        search: filters.search || undefined,
        page: filters.page || undefined,
        per_page: filters.perPage || undefined,
      },
    });

    return {
      logs: response.data.data ?? [],
      meta: response.data.meta ?? {},
      summary: response.data.summary ?? {},
    };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function exportLogs(filters = {}, format = "xlsx") {
  const axios = axiosInstance().primoLab;

  try {
    const response = await axios.get("/cam-production/export", {
      params: {
        month: filters.month || undefined,
        search: filters.search || undefined,
        format,
      },
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type: format === "csv"
        ? "text/csv;charset=utf-8"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const periodLabel = filters.month || new Date().toISOString().slice(0, 10);

    link.href = downloadUrl;
    link.setAttribute("download", `cam-produzione-${periodLabel}.${format}`);

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    await unwrapError(error);
  }
}

export async function createLog(payload) {
  const axios = axiosInstance().primoLab;
  try {
    const response = await axios.post("/cam-production/logs", payload);
    return { log: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function updateLog(logId, payload) {
  const axios = axiosInstance().primoLab;
  try {
    const response = await axios.put(`/cam-production/logs/${logId}`, payload);
    return { log: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function showLog(logId, loading = true) {
  const axios = loading ? axiosInstance().primoLab : axiosInstanceWithoutLoader().primoLab;
  try {
    const response = await axios.get(`/cam-production/logs/${logId}`);
    return { log: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function createPhotoUpload(logId) {
  const axios = axiosInstance().primoLab;
  try {
    const response = await axios.post(`/cam-production/logs/${logId}/photo-upload`);
    return { data: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function removePhoto(logId) {
  const axios = axiosInstance().primoLab;
  try {
    const response = await axios.delete(`/cam-production/logs/${logId}/photo`);
    return { log: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function showPhotoUpload(token, loading = true) {
  const axios = loading ? axiosInstance().primoLab : axiosInstanceWithoutLoader().primoLab;
  try {
    const response = await axios.get(`/cam-production/photo-uploads/${token}`);
    return { data: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function assignPhotoUploadUrl(token, payload, loading = true) {
  const axios = loading ? axiosInstance().primoLab : axiosInstanceWithoutLoader().primoLab;
  try {
    const response = await axios.post(`/cam-production/photo-uploads/${token}/signed-url`, payload);
    return { data: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}

export async function completePhotoUpload(token, payload = {}, loading = true) {
  const axios = loading ? axiosInstance().primoLab : axiosInstanceWithoutLoader().primoLab;
  try {
    const response = await axios.post(`/cam-production/photo-uploads/${token}/complete`, payload);
    return { log: response.data.data };
  } catch (error) {
    await unwrapError(error);
  }
}
